import { Link } from "react-router";
import { twJoin } from "tailwind-merge";

import { useImageResolver } from "@/api/hooks/useImageResolver";
import type { ImageDto } from "@/api/types";
import { ChevronRightIcon } from "@/components/Icons/ChevronRightIcon";
import { FaceFrownIcon } from "@/components/Icons/FaceFrownIcon";
import { Card, cardSurfaceVariants } from "@/design-system/Card";
import { Skeleton } from "@/design-system/Skeleton";
import { useTranslation } from "@/translations/implementation/useTranslation";
import { RuntimeBackgroundImage } from "@/ui-adapters/RuntimeBackgroundImage";

import type { EntityPreviewCardErrorProps } from "./EntityPreviewCard";

type EntityPreviewCardContainerProps = {
  children: React.ReactNode;
  href?: string;
  onClick?: () => void;
};

export function EntityPreviewCardContainer({
  children,
  href,
  onClick,
}: EntityPreviewCardContainerProps): React.ReactNode {
  const className = twJoin(
    "flex w-full flex-col overflow-hidden",
    cardSurfaceVariants({ interactive: Boolean(href || onClick) }),
  );

  if (href) {
    return (
      <Link to={href} onClick={onClick} target="_blank" className={className}>
        {children}
      </Link>
    );
  }

  if (onClick) {
    return (
      <Card as="button" type="button" density="flush" clip interactive onClick={onClick}>
        {children}
      </Card>
    );
  }

  return <article className={twJoin(className, "pointer-events-none")}>{children}</article>;
}

type EntityPreviewCardHeaderProps = {
  children?: React.ReactNode;
  image: ImageDto | undefined;
  imageUrl?: string;
  alt: string;
  presentation?: "default" | "event";
};

function EntityPreviewCardHeader({
  image,
  imageUrl,
  alt,
  children,
  presentation = "default",
}: EntityPreviewCardHeaderProps): React.ReactNode {
  const resolveImage = useImageResolver();

  return (
    <header
      // `id` needed for intuitive CSS selector when styling this component for app preview
      id="entity-preview-card-header"
      className={twJoin(
        "relative flex w-full bg-background-secondary bg-cover bg-center",
        presentation === "event" ? "h-event-card-header" : "aspect-2/1 @sm:aspect-4/1",
      )}
      aria-label={alt}
    >
      <RuntimeBackgroundImage
        imageUrl={imageUrl ?? (image ? resolveImage(image, "intrinsic") : undefined)}
        presentation="transparentFill"
      >
        <section className="flex size-full">{children}</section>
      </RuntimeBackgroundImage>
    </header>
  );
}

type EntityPreviewCardBodyProps = {
  title: string;
  canNavigate?: boolean;
  presentation?: "default" | "event";
} & (
  | {
      subtitle: string | undefined;
      renderSubtitle?: never;
    }
  | {
      subtitle?: never;
      renderSubtitle: () => React.ReactNode | undefined;
    }
);

function EntityPreviewCardBody({
  title,
  subtitle,
  canNavigate,
  presentation = "default",
  renderSubtitle,
}: EntityPreviewCardBodyProps): React.ReactNode {
  return (
    // `id` needed for intuitive CSS selector when styling this component for app preview
    <section
      id="entity-preview-card-body"
      className={twJoin(
        "flex w-full gap-space-sm p-space-md",
        presentation === "event" ? "items-start" : "items-center",
      )}
    >
      <section
        className={twJoin("flex min-w-0 flex-1 flex-col", presentation === "event" ? "gap-space-xs" : "gap-space-sm")}
      >
        <h4 className={twJoin("text-body-emphasis", presentation === "event" ? "line-clamp-2" : "line-clamp-1")}>
          {title}
        </h4>
        {subtitle ? <p className="line-clamp-1 text-caption text-text-secondary">{subtitle}</p> : renderSubtitle?.()}
      </section>
      {canNavigate && <ChevronRightIcon size={24} tone="secondary" />}
    </section>
  );
}

type EntityPreviewCardWithBackgroundProps = {
  href?: string;
  onClick?: () => void;
  presentation?: "default" | "event";
  headerBackgroundImage: ImageDto | undefined;
  /** Used when neither headerBackgroundImageUrl nor headerBackgroundImage is available. */
  fallbackBackgroundImage?: ImageDto;
  headerBackgroundImageUrl?: string;
  renderHeader?: () => React.ReactNode;
  title: string;
} & (
  | { subtitle: string | undefined; renderSubtitle?: never }
  | { subtitle?: never; renderSubtitle: () => React.ReactNode | undefined }
);

export function EntityPreviewCardWithBackground({
  href,
  onClick,
  presentation = "default",
  headerBackgroundImage,
  fallbackBackgroundImage,
  headerBackgroundImageUrl,
  renderHeader,
  title,
  subtitle,
  renderSubtitle,
}: EntityPreviewCardWithBackgroundProps): React.ReactNode {
  const canNavigate = Boolean(href || onClick);

  return (
    <EntityPreviewCardContainer href={href} onClick={onClick}>
      {/* Header */}
      <EntityPreviewCardHeader
        presentation={presentation}
        image={headerBackgroundImage ?? fallbackBackgroundImage}
        imageUrl={headerBackgroundImageUrl}
        alt={title}
      >
        {renderHeader?.()}
      </EntityPreviewCardHeader>
      {/* Body */}
      {subtitle && (
        <EntityPreviewCardBody
          title={title}
          subtitle={subtitle}
          presentation={presentation}
          canNavigate={canNavigate}
        />
      )}
      {renderSubtitle && (
        <EntityPreviewCardBody
          title={title}
          renderSubtitle={renderSubtitle}
          presentation={presentation}
          canNavigate={canNavigate}
        />
      )}
    </EntityPreviewCardContainer>
  );
}

export function EntityPreviewCardWithBackgroundSkeleton(): React.ReactNode {
  return (
    <EntityPreviewCardContainer>
      {/* Header */}
      <header className="aspect-4/1">
        <Skeleton width="full" height="full" />
      </header>
      {/* Body */}
      <section className="flex w-full flex-col items-start gap-space-sm p-space-md">
        <Skeleton height="6" width="1/3" />
        <Skeleton height="5" width="2/3" />
      </section>
    </EntityPreviewCardContainer>
  );
}

export function EntityPreviewCardWithBackgroundError({
  title,
  subtitle,
}: EntityPreviewCardErrorProps): React.ReactNode {
  const { t } = useTranslation();

  const titleOverride = title || t("component.community-post.entity-preview.title.not-available");
  const subtitleOverride = subtitle || t("component.community-post.entity-preview.subtitle.not-available");

  return (
    <section className="w-full *:text-text-secondary">
      <EntityPreviewCardContainer>
        <EntityPreviewCardHeader image={undefined} alt="">
          <section className="inset-0 flex flex-1 items-center justify-center">
            <FaceFrownIcon size={48} />
          </section>
        </EntityPreviewCardHeader>
        <EntityPreviewCardBody title={titleOverride} subtitle={subtitleOverride} />
      </EntityPreviewCardContainer>
    </section>
  );
}
